import { useCallback } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';

import config from 'constants/apiConfig';
import { FEMA_REGION_META, getEpaRrtAorsFallback } from 'constants/epaRrtAors';
import { OPORD_TEMPLATE_ID } from 'components/DisasterGPT/constants/opordSectionDefaults';
import {
  fetchSitrepById,
  useAddSitrepSectionV2,
  useInitSitrepV2,
  useUpdateSitrepSectionV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import { useUpsertAor } from 'hooks/useAors';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import type { AreaOfResponsibility } from 'types/aor';
import generateUUID from 'utils/sharedUtils/generateUUID';

function agentDebugLog(payload: Record<string, unknown>) {
  // #region agent log
  fetch('http://127.0.0.1:7250/ingest/d34173ea-f10f-4b43-b38f-5b4d5b60606c', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Debug-Session-Id': '30a1ee',
    },
    body: JSON.stringify({
      sessionId: '30a1ee',
      timestamp: Date.now(),
      ...payload,
    }),
  }).catch(() => {});
  // #endregion
}

import {
  FEMA_MANUAL_SECTION_TEMPLATE_ID,
  FEMA_NATIONAL_SECTIONS,
  FEMA_OPS_PRESENTATION_NAME,
  FEMA_REGION_OPORD_HTML,
  OPORD_GENERAL_SECTION_TEMPLATE_ID,
  femaOpordName,
  type FemaRegionRoman,
} from './femaOpsBriefing';

const OPORD_REGIONS: FemaRegionRoman[] = ['II', 'IV', 'IX'];

export type FemaOpsSeedResult = {
  nationalReportId: string;
  regionReportIds: Record<FemaRegionRoman, string>;
};

async function persistedAorIds(groupGuid: string): Promise<Set<string>> {
  const response = await axios.post<AreaOfResponsibility[]>(
    config.apiGateway.getAllAor,
    { group_guid: groupGuid }
  );
  return new Set(response.data.map((aor) => aor.id));
}

export function useEnsureFemaOpsBriefing() {
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const initSitrep = useInitSitrepV2().mutateAsync;
  const addSection = useAddSitrepSectionV2().mutateAsync;
  const updateSection = useUpdateSitrepSectionV2().mutateAsync;
  const upsertAor = useUpsertAor().mutateAsync;

  return useCallback(
    async (sitreps: GetSitrepsResponse[]): Promise<FemaOpsSeedResult> => {
      if (!groupGuid || !userGuid) {
        throw new Error('Open an organization before seeding the briefing.');
      }

      let step = 'aors';
      try {
      const existingIds = await persistedAorIds(groupGuid);
      const fallbackAors = getEpaRrtAorsFallback(groupGuid);
      for (const meta of FEMA_REGION_META) {
        if (existingIds.has(meta.id)) continue;
        const aor = fallbackAors.find((entry) => entry.id === meta.id);
        if (!aor) continue;
        await upsertAor(aor);
      }

      step = 'national-init';
      const active = sitreps.filter((sitrep) => !sitrep.archived);
      const national = active.find(
        (sitrep) => sitrep.name === FEMA_OPS_PRESENTATION_NAME && !sitrep.aor_id
      );
      let nationalReportId = national?.id;
      if (!nationalReportId) {
        const sitrepId = await initSitrep({
          name: FEMA_OPS_PRESENTATION_NAME,
          description: 'FEMA National Watch Center daily operations briefing',
          sitrep_source: 'reports',
          sitrep_source_id: generateUUID(),
          selectedDatetime: new Date().toISOString(),
          skip_default_sections: true,
          is_sitrep: true,
        });
        step = 'national-section';
        for (const [index, section] of FEMA_NATIONAL_SECTIONS.entries()) {
          await addSection({
            sitrepId,
            sectionTemplateId: FEMA_MANUAL_SECTION_TEMPLATE_ID,
            selectedDatetime: new Date().toISOString(),
            sitrep_source: 'reports',
            sitrep_source_id: generateUUID(),
            order: index + 1,
            name: section.name,
            skipProcessing: true,
            initialSectionText: section.html,
          });
        }
        nationalReportId = sitrepId;
      }

      const regionReportIds = {} as Record<FemaRegionRoman, string>;
      for (const roman of OPORD_REGIONS) {
        const meta = FEMA_REGION_META.find((entry) => entry.roman === roman);
        if (!meta) throw new Error(`Missing FEMA region ${roman}`);
        step = `opord-${roman}`;
        const name = femaOpordName(roman);
        const existing = active.find(
          (sitrep) => sitrep.name === name && sitrep.aor_id === meta.id
        );
        if (existing) {
          regionReportIds[roman] = existing.id;
          continue;
        }
        const sitrepId = await initSitrep({
          name,
          description: '',
          sitrep_template_id: OPORD_TEMPLATE_ID,
          sitrep_source: 'reports',
          sitrep_source_id: generateUUID(),
          selectedDatetime: new Date().toISOString(),
          aor_id: meta.id,
          is_sitrep: true,
        });
        const created = await fetchSitrepById(sitrepId);
        const general = created.sections?.find(
          (section) =>
            section.section_template_id === OPORD_GENERAL_SECTION_TEMPLATE_ID
        );
        if (general) {
          await updateSection({
            sitrepId,
            sectionId: general.id,
            silent: true,
            updatedFields: { sectionText: FEMA_REGION_OPORD_HTML[roman] },
          });
        }
        regionReportIds[roman] = sitrepId;
      }

      if (!nationalReportId) {
        throw new Error('The national briefing report was not created.');
      }
      // #region agent log
      agentDebugLog({
        runId: 'post-fix',
        hypothesisId: 'C',
        location: 'femaOpsSeed.ts:ensure',
        message: 'briefing records ready',
        data: {
          hasNational: Boolean(nationalReportId),
          regionCount: Object.keys(regionReportIds).length,
        },
      });
      // #endregion
      return {
        nationalReportId,
        regionReportIds,
      };
      } catch (error) {
        // #region agent log
        agentDebugLog({
          runId: 'post-fix',
          hypothesisId: 'C',
          location: 'femaOpsSeed.ts:ensure',
          message: 'briefing seed threw',
          data: {
            step,
            errorMessage: error instanceof Error ? error.message : 'unknown',
            status:
              error && typeof error === 'object' && 'response' in error
                ? (error as { response?: { status?: number; data?: { error?: string } } }).response?.status ?? null
                : null,
            serverError:
              error && typeof error === 'object' && 'response' in error
                ? (error as { response?: { data?: { error?: string } } }).response?.data?.error ?? null
                : null,
          },
        });
        // #endregion
        throw error;
      }
    },
    [addSection, groupGuid, initSitrep, updateSection, upsertAor, userGuid]
  );
}
